import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import InterestSerivce from '../../../../service/Interest';
import { CaseInfo } from '../../data';
import { ReportFormatNumber } from '../../../../func/Function';
import { NplCalculation } from '../../../../utils/nplAnalysis';
import { INplInfo } from '../../../../service/detail/Detail';
import { s } from './NPL.styles';

interface INPLSummaryProps {
  caseInfo: CaseInfo;
  summary: ReturnType<NplCalculation['getSummary']>;
  info: INplInfo;
}

const ItemRow = ({
  label,
  content,
  action,
}: {
  label?: React.ReactNode;
  content: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <s.Item>
    {label && <s.ItemLabel>{label}</s.ItemLabel>}
    <s.ItemContent>{content}</s.ItemContent>
    {action}
  </s.Item>
);

const MetricItem = ({
  label,
  num,
  percent,
  isBlue,
}: {
  label: React.ReactNode;
  num: number;
  percent: number | string;
  isBlue?: boolean;
}) => (
  <s.Item>
    <s.ItemLabel style={isBlue ? { color: '#008AFF' } : undefined}>
      {label}
    </s.ItemLabel>
    <Right>
      <s.ItemContent style={isBlue ? { color: '#008AFF' } : undefined}>
        {ReportFormatNumber(num)}
      </s.ItemContent>
      <PercentBadge
        style={
          isBlue
            ? { color: '#008AFF', backgroundColor: 'rgba(0, 138, 255, 0.06)' }
            : undefined
        }
      >
        {typeof percent === 'string'
          ? percent
          : ReportFormatNumber(percent, true)}
      </PercentBadge>
    </Right>
  </s.Item>
);
const formatDate = (v?: string) => {
  if (!v) return '-';
  const d = v.split(' ')[0];
  return d.replaceAll('-', '.');
};

const NPLSummary = (props: INPLSummaryProps) => {
  const { summary, info } = props;

  const [isLikeItem, setIsLikeItem] = useState(false);
  const [showLikeModal, setShowLikeModal] = useState(false);
  const [mode] = useState<string>('add');
  const [selectedItem] = useState({
    iidx: 0,
    group: '',
    group_idx: 0,
    level: 0,
    memo: '',
  });
  const [activeItem, setActiveItem] = useState<number | null>(null);

  const checkIsLikeItem = async (iidx: number) => {
    try {
      const res = await InterestSerivce.check(iidx);
      setIsLikeItem(res.data.is_like);
    } catch (error) {
      console.error('checkIsLikeItem 함수에서 에러 발생:', error);
    }
  };

  useEffect(() => {
    if (props.caseInfo?.iidx) {
      checkIsLikeItem(props.caseInfo.iidx);
    }
  }, [props.caseInfo?.iidx]);

  const handleCopy = (
    text?: string,
    okMsg = '복사되었습니다!',
    failMsg = '복사에 실패했습니다.',
  ) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => alert(okMsg))
      .catch(() => alert(failMsg));
  };

  const addressCopyBtn = info.address && (
    <s.CopyAddress
      onClick={() =>
        handleCopy(props.caseInfo?.iaddr, '주소가 복사되었습니다!')
      }
    >
      <img
        src="/assets/images/icon/copy-blue.svg"
        alt=""
        width={16}
        height={16}
      />
      <span>주소복사</span>
    </s.CopyAddress>
  );

  const caseNoCopyBtn = info.caseno && (
    <s.CopyAddress
      onClick={() => handleCopy(info.caseno, '사건번호가 복사되었습니다!')}
    >
      <img
        src="/assets/images/icon/copy-blue.svg"
        alt=""
        width={16}
        height={16}
      />
      <span>사건번호복사</span>
    </s.CopyAddress>
  );

  const safeNum = (v: unknown) =>
    typeof v === 'number' && isFinite(v) ? v : 0;

  const percentOfComposite = (value?: number | null): number | string => {
    const base = summary.row4_1;
    const v = safeNum(value);
    if (base <= 0 || v <= 0) return '-'; // 0이거나 유효하지 않으면 표시 생략
    return (v / base) * 100;
  };

  const infoRows = useMemo(
    () => [
      { content: info.address, action: addressCopyBtn }, // 라벨 없음 + 복사
      { content: info.caseno, action: caseNoCopyBtn }, // 라벨 없음 + 복사
      { label: '법원', content: info.court },
      { label: '채권자', content: info.creditee },
      { label: '채무자 (소유자)', content: info.rightholder },
      { label: '경매개시일', content: formatDate(info.start_date) },
      { label: '배당종기일', content: formatDate(info.div_date) },
    ],
    [info, addressCopyBtn, caseNoCopyBtn],
  );

  const metrics = useMemo(
    () => [
      {
        label: '법원감정가격',
        num: summary.row1,
        percent: percentOfComposite(summary.row1),
      },
      {
        label: 'AI 시세추정',
        num: Math.round(Number(summary.row2_1)),
        percent: percentOfComposite(summary.row2_1),
      },
      {
        label: '채권청구액',
        num: summary.row3_1,
        percent: percentOfComposite(summary.row3_1),
        isBlue: true,
      },
      {
        label: '예상 낙찰가 (통계)',
        num: summary.row5_1,
        percent: percentOfComposite(summary.row5_1),
      },
      {
        label: '예상 낙찰가 (AI)',
        num: summary.row6_1,
        percent: percentOfComposite(summary.row6_1),
      },
      {
        label: '예상 낙찰가 (종합)',
        num: summary.row4_1,
        percent: percentOfComposite(summary.row4_1),
        isBlue: true,
      },
    ],
    [summary],
  );

  return (
    <s.Div style={{ marginBottom: 0 }}>
      <s.Title>NPL 분석요약</s.Title>
      <div>
        {infoRows.map((row, idx) => (
          <ItemRow
            key={`info-${idx}`}
            label={row.label as any}
            content={row.content as any}
            action={row.action as any}
          />
        ))}
      </div>
      <s.GrayBox>
        {metrics.map((m, idx) => (
          <MetricItem
            key={`metric-${idx}`}
            label={m.label}
            num={m.num as number}
            percent={m.percent as number | string}
            isBlue={(m as any).isBlue}
          />
        ))}
      </s.GrayBox>
    </s.Div>
  );
};

export default NPLSummary;

const PercentBadge = styled.div`
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(112, 115, 124, 0.08);
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-weight: 700;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;
